import {
  Button,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  Field,
  Input,
  Select,
  SkeletonRows,
  StatusBadge,
  THead,
  TableShell,
  Td,
  Th,
  Tr,
  cn,
  useToast,
} from '@ambernord/ui';
import { Boxes } from 'lucide-react';
import { type FormEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AppLayout, DataState } from '../components/AppLayout.js';
import { api } from '../lib/api.js';
import { formatDate, productName } from '../lib/format.js';

interface Batch {
  id: string;
  lotNo: string;
  productId: string;
  productionDate: string | null;
  bestBefore: string | null;
}

interface ProductItem {
  id: string;
  sku: string;
  namesI18n: Record<string, string>;
}

interface BatchDetail {
  batch: unknown;
  traceabilityRecords: unknown[];
  coas: Array<{ id: string; overallResult: string | null; status: string }>;
  declarations: Array<{ id: string; type: string }>;
}

function productLabel(products: ProductItem[], id: string, locale: string): string {
  const p = products.find((x) => x.id === id);
  if (!p) return id;
  return productName(p.namesI18n, locale, p.sku);
}

export default function Traceability() {
  const { t, i18n } = useTranslation('traceability');
  const { toast } = useToast();
  const [batches, setBatches] = useState<Batch[] | null>(null);
  const [products, setProducts] = useState<ProductItem[]>([]);
  const [error, setError] = useState<string | null>(null);

  const [productId, setProductId] = useState('');
  const [lotNo, setLotNo] = useState('');
  const [productionDate, setProductionDate] = useState('');
  const [bestBefore, setBestBefore] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<BatchDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);

  const statusLabel = (code: string) => t(`statuses:${code.toLowerCase()}`, { defaultValue: code });

  async function reloadBatches() {
    const res = await api.get<{ items: Batch[] }>('/api/traceability/batches');
    if (res.ok) setBatches(res.data.items ?? []);
  }

  useEffect(() => {
    let alive = true;
    void (async () => {
      const res = await api.get<{ items: Batch[] }>('/api/traceability/batches');
      if (!alive) return;
      if (!res.ok) {
        setError(t('loadError'));
        return;
      }
      setBatches(res.data.items ?? []);
    })();
    void (async () => {
      const res = await api.get<{ items: ProductItem[] }>('/api/products');
      if (!alive || !res.ok) return;
      const items = res.data.items ?? [];
      setProducts(items);
      if (items[0]) setProductId(items[0].id);
    })();
    return () => {
      alive = false;
    };
  }, [t]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setFormError(null);
    if (!productId) {
      setFormError(t('errors.selectProduct'));
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post<{ error?: string }>('/api/traceability/batches', {
        productId,
        ...(lotNo.trim() ? { lotNo: lotNo.trim() } : {}),
        ...(productionDate ? { productionDate: new Date(productionDate).toISOString() } : {}),
        ...(bestBefore ? { bestBefore: new Date(bestBefore).toISOString() } : {}),
      });
      if (!res.ok) {
        const code = res.data.error ?? '';
        const known: Record<string, string> = {
          invalid_lot_no: t('errors.invalidLotNo'),
          lot_exists: t('errors.lotExists'),
        };
        const msg = known[code] ?? t('errors.createFailed', { code: code || res.status });
        setFormError(msg);
        toast(msg, 'error');
        return;
      }
      setLotNo('');
      setProductionDate('');
      setBestBefore('');
      toast(t('toast.created'));
      await reloadBatches();
    } catch {
      const msg = t('errors.createFailedRetry');
      setFormError(msg);
      toast(msg, 'error');
    } finally {
      setSubmitting(false);
    }
  }

  async function openDetail(id: string) {
    if (selectedId === id) {
      setSelectedId(null);
      setDetail(null);
      return;
    }
    setSelectedId(id);
    setDetail(null);
    setDetailError(null);
    setDetailLoading(true);
    try {
      const res = await api.get<BatchDetail>(`/api/traceability/batches/${id}`);
      if (!res.ok) {
        setDetailError(t('detail.loadError'));
        return;
      }
      setDetail(res.data);
    } finally {
      setDetailLoading(false);
    }
  }

  return (
    <AppLayout title={t('title')}>
      <p className="mb-6 max-w-2xl text-sm text-slate-600 dark:text-slate-300">{t('intro')}</p>

      <div className="grid gap-8 lg:grid-cols-3">
        <section className="lg:col-span-2">
          <h2 className="mb-3 text-base font-medium">{t('list.heading')}</h2>
          {error ? (
            <DataState loading={false} error={error} empty={false}>
              {null}
            </DataState>
          ) : batches === null ? (
            <SkeletonRows rows={5} />
          ) : batches.length === 0 ? (
            <EmptyState
              icon={<Boxes className="h-6 w-6" />}
              title={t('empty.title')}
              hint={t('empty.hint')}
            />
          ) : (
            <TableShell>
              <THead>
                <tr>
                  <Th>{t('list.columns.lotNo')}</Th>
                  <Th>{t('list.columns.product')}</Th>
                  <Th>{t('list.columns.productionDate')}</Th>
                  <Th>{t('list.columns.bestBefore')}</Th>
                </tr>
              </THead>
              <tbody>
                {batches.map((b) => {
                  const selectedCls =
                    selectedId === b.id ? 'bg-slate-50 dark:bg-slate-800/50' : undefined;
                  return (
                    <Tr
                      key={b.id}
                      interactive
                      onClick={() => void openDetail(b.id)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          void openDetail(b.id);
                        }
                      }}
                    >
                      <Td className={cn('font-medium text-slate-900 dark:text-white', selectedCls)}>
                        {b.lotNo}
                      </Td>
                      <Td className={selectedCls}>
                        {productLabel(products, b.productId, i18n.language)}
                      </Td>
                      <Td className={selectedCls}>{formatDate(b.productionDate, i18n.language)}</Td>
                      <Td className={selectedCls}>{formatDate(b.bestBefore, i18n.language)}</Td>
                    </Tr>
                  );
                })}
              </tbody>
            </TableShell>
          )}

          {selectedId && (
            <Card className="mt-4">
              <CardHeader title={t('detail.heading')} />
              <CardBody>
                {detailLoading ? (
                  <SkeletonRows rows={3} />
                ) : detailError ? (
                  <p className="text-sm text-red-600 dark:text-red-400">{detailError}</p>
                ) : detail ? (
                  <div className="flex flex-col gap-4 text-sm">
                    <div>
                      <p className="mb-1 font-medium">
                        {t('detail.coas', { n: detail.coas.length })}
                      </p>
                      {detail.coas.length === 0 ? (
                        <p className="text-slate-500 dark:text-slate-400">{t('detail.none')}</p>
                      ) : (
                        <ul className="flex flex-col gap-1.5">
                          {detail.coas.map((c) => (
                            <li key={c.id} className="flex items-center gap-2">
                              <StatusBadge status={c.status} label={statusLabel(c.status)} />
                              {c.overallResult ? (
                                <StatusBadge
                                  status={c.overallResult.toLowerCase()}
                                  label={statusLabel(c.overallResult)}
                                />
                              ) : (
                                <span className="text-slate-400 dark:text-slate-500">—</span>
                              )}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                    <div>
                      <p className="mb-1 font-medium">
                        {t('detail.declarations', { n: detail.declarations.length })}
                      </p>
                      {detail.declarations.length === 0 ? (
                        <p className="text-slate-500 dark:text-slate-400">{t('detail.none')}</p>
                      ) : (
                        <ul className="flex flex-col gap-1">
                          {detail.declarations.map((d) => (
                            <li key={d.id} className="font-medium">
                              {d.type}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                    <p className="text-slate-500 dark:text-slate-400">
                      {t('detail.records', { n: detail.traceabilityRecords.length })}
                    </p>
                  </div>
                ) : null}
              </CardBody>
            </Card>
          )}
        </section>

        <section>
          <Card>
            <CardHeader title={t('form.heading')} />
            <CardBody>
              <form className="flex flex-col gap-4" onSubmit={(e) => void onSubmit(e)} noValidate>
                <Field label={t('form.productLabel')} htmlFor="batch-product">
                  <Select
                    id="batch-product"
                    required
                    value={productId}
                    onChange={(e) => setProductId(e.target.value)}
                  >
                    {products.length === 0 ? (
                      <option value="">{t('form.noProducts')}</option>
                    ) : (
                      products.map((p) => (
                        <option key={p.id} value={p.id}>
                          {productName(p.namesI18n, i18n.language, p.sku)}
                        </option>
                      ))
                    )}
                  </Select>
                </Field>
                <Field label={t('form.lotLabel')} htmlFor="batch-lot">
                  <Input
                    id="batch-lot"
                    type="text"
                    placeholder={t('form.lotPlaceholder')}
                    value={lotNo}
                    onChange={(e) => setLotNo(e.target.value)}
                  />
                </Field>
                <Field label={t('form.productionDateLabel')} htmlFor="batch-prod-date">
                  <Input
                    id="batch-prod-date"
                    type="date"
                    value={productionDate}
                    onChange={(e) => setProductionDate(e.target.value)}
                  />
                </Field>
                <Field label={t('form.bestBeforeLabel')} htmlFor="batch-best-before">
                  <Input
                    id="batch-best-before"
                    type="date"
                    value={bestBefore}
                    onChange={(e) => setBestBefore(e.target.value)}
                  />
                </Field>
                {formError && (
                  <p className="text-sm text-red-600 dark:text-red-400" role="alert">
                    {formError}
                  </p>
                )}
                <Button type="submit" disabled={submitting || products.length === 0}>
                  {submitting ? t('form.submitting') : t('form.submit')}
                </Button>
              </form>
            </CardBody>
          </Card>
        </section>
      </div>
    </AppLayout>
  );
}
